import { Badge } from "@/components/ui/badge";
import { useTranslation } from "@/hooks/useTranslation";
import type { ContextWindowUsage, LLMModels } from "@shaarp/common";
import {
  compactionKindLabelKey,
  turnCompactionKind,
  turnCompactionVariant,
} from "./compaction";
import type { ContextGap, SegmentRow } from "./context-segments";
import { SegmentDot } from "./debug-tab-parts";
import {
  formatDelta,
  formatPercent,
  fullNumber,
} from "./format";

type ContextTabProps = {
  usage: ContextWindowUsage;
  llmId?: LLMModels;
  rows: SegmentRow[];
  gap: ContextGap | null;
};

/** Where the prompt's tokens went, block by block, for the turn just answered. */
export function ContextTab({ usage, llmId, rows, gap }: ContextTabProps) {
  const { t } = useTranslation();
  const { contextWindowSize, estimatedTokensUsed, compactionTier } = usage;
  const turnKind = turnCompactionKind(compactionTier);

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <div className="flex items-baseline justify-between gap-3">
          <span className="font-mono text-2xl">
            {formatPercent(estimatedTokensUsed, contextWindowSize)}
          </span>
          <span className="font-mono text-muted-foreground text-xs">
            {fullNumber(estimatedTokensUsed)} /{" "}
            {fullNumber(contextWindowSize)} tokens
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {llmId && <Badge variant="secondary">{llmId}</Badge>}
          <Badge variant={turnCompactionVariant(compactionTier)}>
            {turnKind
              ? t(compactionKindLabelKey(turnKind))
              : t("chat.debug.compaction.turnNone")}
          </Badge>
        </div>
      </header>

      {rows.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          {t("chat.debug.context.noBreakdown")}
        </p>
      ) : (
        <>
          <SegmentBar rows={rows} total={contextWindowSize} />
          <SegmentList rows={rows} total={contextWindowSize} />
          {gap && <GapNote gap={gap} estimate={estimatedTokensUsed} />}
        </>
      )}
    </div>
  );
}

function SegmentBar({ rows, total }: { rows: SegmentRow[]; total: number }) {
  const { t } = useTranslation();

  return (
    <div className="flex h-3 w-full overflow-hidden rounded-full">
      {rows.map((row) => (
        <div
          key={row.key}
          style={{
            backgroundColor: row.color,
            width: `${(row.tokens / total) * 100}%`,
          }}
          title={`${t(row.labelKey)} — ${fullNumber(row.tokens)}`}
        />
      ))}
    </div>
  );
}

function SegmentList({ rows, total }: { rows: SegmentRow[]; total: number }) {
  const { t } = useTranslation();

  return (
    <div className="divide-y">
      {rows.map((row) => (
        <div className="space-y-1 py-2.5" key={row.key}>
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="flex min-w-0 items-center gap-2">
              <SegmentDot color={row.color} size="md" />
              <span className="truncate">{t(row.labelKey)}</span>
            </span>
            <span className="shrink-0 font-mono text-xs">
              {fullNumber(row.tokens)}
              <span className="ml-2 text-muted-foreground">
                {formatPercent(row.tokens, total)}
              </span>
            </span>
          </div>
          <p className="pl-4.5 text-muted-foreground text-xs">
            {t(row.hintKey)}
          </p>
        </div>
      ))}
    </div>
  );
}

function GapNote({ gap, estimate }: { gap: ContextGap; estimate: number }) {
  const { t } = useTranslation();

  return (
    <div className="space-y-1.5 rounded-md border bg-muted/40 p-3">
      <p className="font-medium text-sm">
        {t("chat.debug.context.gapTitle")}
      </p>
      <GapLine
        label={t("chat.debug.context.gapEstimate")}
        value={`${fullNumber(estimate)} (${gap.estimatePercent})`}
      />
      <GapLine
        label={t("chat.debug.context.gapBreakdown")}
        value={`${fullNumber(gap.breakdownUsed)} (${gap.breakdownPercent})`}
      />
      <GapLine
        label={t("chat.debug.context.gapDelta")}
        value={formatDelta(gap.delta)}
      />
      <p className="text-muted-foreground text-xs">
        {t("chat.debug.context.gapNote")}
      </p>
    </div>
  );
}

function GapLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 font-mono text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span>{value}</span>
    </div>
  );
}
